(() => {
  'use strict';
  const node = document.querySelector('[data-ucw-printer-config]');
  if (!node || window.UCWPrinters || !window.UCWPrinterAccount) return;
  const config = { ...node.dataset, accountEnabled: node.dataset.accountEnabled === 'true' };
  const account = new UCWPrinterAccount(config), model = UCWPrinterModel;
  let state = { status: 'loading', printers: [], error: '' }, refreshTask, revision = 0, storefrontData, sessionTask, lastSessionCheck = 0, cacheTimer, sessionGeneration = 0, accountNavigation = false;
  const catalog = new Map();
  const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('ucw-printer-account') : null;
  const errors = {
    account_unavailable: 'Uw printers kunnen tijdelijk niet worden geladen. Probeer het later opnieuw.',
    login_required: 'Log in om uw printers te bekijken en op te slaan.',
    login_invalid: 'Het inloggen is niet afgerond. Probeer opnieuw.',
    account_conflict: 'Uw lijst is ondertussen gewijzigd. Vernieuw uw printers en probeer opnieuw.',
    printer_data_invalid: 'Uw printerlijst kan niet worden gelezen. Neem contact met ons op; uw gegevens zijn behouden.',
    printer_limit: 'U heeft het maximum van 250 printers bereikt.',
    printer_invalid: 'Deze printer is niet gevonden. Zoek opnieuw op merk en typenummer.',
    account_error: 'Uw printers konden niet worden bijgewerkt. Probeer opnieuw.'
  };
  const snapshot = () => model.clone(state);
  const message = error => (errors[error.message] || errors.account_error) + (error.reference ? ' Referentie: ' + error.reference + '.' : '');
  function scheduleRefresh() {
    if (cacheTimer) window.clearTimeout?.(cacheTimer);
    const cache = account.cachedLists?.();
    if (!cache || !window.setTimeout || state.status !== 'ready') return;
    cacheTimer = window.setTimeout(() => { if (!document.hidden) refresh(); }, Math.max(1000, cache.expiresAt - Date.now()));
  }
  function publish(next) { const changed = JSON.stringify(state) !== JSON.stringify(next); state = next; scheduleRefresh(); if (!changed) return; revision++; document.dispatchEvent(new CustomEvent('ucw:printers-updated', { detail: snapshot() })); }
  function clearSignedOut(error = '') {
    sessionGeneration++; accountNavigation = false; account.clear(); catalog.clear(); storefrontData = undefined;
    account.config.customerId = '';
    publish({ status: 'signed-out', printers: [], error });
  }
  function fail(error) {
    if (error.message === 'session_superseded') return;
    const signedOut = ['login_required', 'login_invalid'].includes(error.message);
    if (signedOut) { clearSignedOut(message(error)); channel?.postMessage({ type: 'logout' }); return; }
    if (!signedOut && state.status === 'ready') { publish({ ...state, error: message(error) }); return; }
    publish({ status: signedOut ? 'signed-out' : 'error', printers: [], error: message(error) });
  }
  async function syncStorefrontSession(includePrinters = false) {
    const generation = sessionGeneration;
    const response = await fetch('/pages/uwprinters?view=' + (includePrinters ? 'ucw-printer-bootstrap' : 'ucw-printer-session'), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error('account_error');
    const session = await response.json();
    if (generation !== sessionGeneration) throw new Error('session_superseded');
    if (typeof session.customerId !== 'string' || !/^\d*$/.test(session.customerId)) throw new Error('account_error');
    if (account.tokens && (!session.customerId || (account.customerId && session.customerId !== account.customerId))) throw new Error('login_required');
    account.config.customerId = session.customerId;
    if (includePrinters) storefrontData = session.customerId && session.printerData != null ? model.normalise(session.printerData) : undefined;
    lastSessionCheck = Date.now();
  }
  async function refresh({ sessionChecked = false, preferStorefront = false } = {}) {
    if (refreshTask) return refreshTask;
    const generation = sessionGeneration;
    refreshTask = (async () => {
      if (state.status !== 'ready') publish({ status: 'loading', printers: [], error: '' });
      try {
        if (!sessionChecked) await syncStorefrontSession();
        let result;
        if (preferStorefront && storefrontData && !(Date.now() - (account.tokens?.listChangedAt || 0) < 15000) && !account.resume && (!account.customerId || account.customerId === account.config.customerId)) result = { data: storefrontData };
        else { account.invalidateLists(); result = await account.read(); }
        if (generation !== sessionGeneration) return snapshot();
        const resume = account.resume;
        if (resume?.pendingHandle) {
          const printer = await resolve(resume.pendingHandle);
          const data = result.data.printers.some(item => item.id === printer.id) ? result.data : await account.apply({ type: 'add', printer });
          if (!data.printers.some(item => item.id === printer.id)) throw new Error('account_error');
          result = { ...result, data };
          channel?.postMessage({ type: 'changed' });
        }
        if (generation !== sessionGeneration) return snapshot();
        // Consume the verified login intent only after Shopify confirms the saved list.
        account.resume = null;
        if (resume?.returnTo && resume.returnTo !== location.pathname) {
          publish({ status: 'redirecting', printers: [], error: '' });
          location.replace(resume.returnTo); return snapshot();
        }
        if (preferStorefront && result.data === storefrontData) account.cacheLists?.(result.data);
        publish({ status: 'ready', printers: result.data.printers, popupSeen: (result.data.ui?.popupSessions || []).includes(account.tokens?.sessionId), error: '' });
        return snapshot();
      }
      catch (error) { if (generation === sessionGeneration) fail(error); return snapshot(); }
    })().finally(() => { refreshTask = null; });
    return refreshTask;
  }
  async function resolve(handle) {
    if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) throw new Error('printer_invalid');
    const response = await fetch('/collections/' + encodeURIComponent(handle) + '?view=ucw-printer-data', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error('printer_invalid');
    let data; try { data = await response.json(); } catch { throw new Error('printer_invalid'); }
    if (data.type !== 'printer' || data.handle !== handle) throw new Error('printer_invalid');
    return model.printer(data);
  }
  async function change(action) {
    await ready;
    if (sessionTask) await sessionTask;
    if (refreshTask) await refreshTask;
    if (state.status !== 'ready') throw new Error(state.status === 'signed-out' ? 'login_required' : 'account_error');
    try {
      if (action.type === 'add') action = { type: 'add', printer: await resolve(action.handle) };
      const data = await account.apply(action);
      publish({ status: 'ready', printers: data.printers, popupSeen: (data.ui?.popupSessions || []).includes(account.tokens?.sessionId), error: '' });
      channel?.postMessage({ type: 'changed' });
      return model.clone(data);
    } catch (error) { fail(error); throw error; }
  }
  async function login(options = {}) { return account.login({ ...account.resume, ...options }); }
  async function logout() {
    sessionGeneration++; storefrontData = undefined;
    publish({ status: 'signed-out', printers: [], error: '' }); catalog.clear();
    channel?.postMessage({ type: 'logout' }); await account.logout();
  }
  async function save(handle) {
    await ready;
    if (state.status === 'signed-out' || state.needsAccountLogin) return login({ returnTo: location.pathname, pendingHandle: handle });
    return change({ type: 'add', handle });
  }
  async function products(handle) {
    if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) throw new Error('printer_invalid');
    if (!catalog.has(handle)) {
      const task = (async () => {
        const handles = new Set();
        for (let page = 1; page <= 40; page++) {
          const response = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('account_error');
          const data = await response.json();
          if (!Array.isArray(data.products)) throw new Error('account_error');
          data.products.forEach(item => handles.add(item.handle));
          if (data.products.length < 250) return handles;
        }
        throw new Error('account_error');
      })().catch(error => { catalog.delete(handle); throw error; });
      catalog.set(handle, task);
    }
    return catalog.get(handle);
  }
  async function match(productHandle) {
    await ready;
    const version = revision, printers = snapshot().printers;
    if (state.status !== 'ready' || !printers.length) return { status: 'empty', printers: [], matches: [] };
    const results = await Promise.allSettled(printers.map(async printer => ({ printer, fits: (await products(printer.handle)).has(productHandle) })));
    if (version !== revision) return { status: 'stale', printers: [], matches: [] };
    const matches = results.filter(row => row.status === 'fulfilled' && row.value.fits).map(row => row.value.printer);
    return { status: matches.length ? 'match' : results.some(row => row.status === 'rejected') ? 'unknown' : 'no-match', printers, matches };
  }
  const ready = (async () => {
    const generation = sessionGeneration;
    if (!account.enabled) { fail(new Error('account_unavailable')); return; }
    try {
      const immediate = account.restoreCachedSession?.();
      if (immediate) {
        publish({ status: 'ready', printers: immediate.data.printers, error: '' });
        sessionTask = syncStorefrontSession().catch(fail).finally(() => { sessionTask = null; });
        return;
      }
      await syncStorefrontSession(!account.readSession?.('ucw-printer:list-cache:v1'));
      const onAccountPage = Boolean(document.querySelector('[data-ucw-printer-account]'));
      const authenticated = await account.initialise({ syncSession: onAccountPage });
      if (generation !== sessionGeneration || authenticated === 'redirecting') return;
      if (!authenticated) {
        if (storefrontData) publish({ status: 'ready', printers: storefrontData.printers, needsAccountLogin: true, error: '' });
        else publish({ status: 'signed-out', printers: [], error: '' });
        return;
      }
      const cached = !account.resume && account.cachedLists?.({ allowExpired: true });
      if (cached) {
        publish({ status: 'ready', printers: cached.data.printers, error: '' });
        if (cached.expiresAt > Date.now()) return;
      }
      await refresh({ sessionChecked: true, preferStorefront: !cached });
    } catch (error) { if (generation === sessionGeneration) fail(error); }
  })();
  window.UCWPrinters = Object.freeze({ ready, snapshot, refresh, resolve, match, products, login, logout, save, add: handle => change({ type: 'add', handle }), remove: id => change({ type: 'remove', id }), markPopupSeen: () => change({ type: 'popup_seen', sessionKey: account.tokens?.sessionId }), message });
  channel?.addEventListener('message', event => {
    if (event.data?.type === 'logout') clearSignedOut();
    else if (event.data?.type === 'changed' && account.tokens) ready.then(() => { if (state.status !== 'redirecting') refresh(); });
  });
  function checkSession(force = false) {
    if (sessionTask || state.status === 'redirecting' || !account.tokens || !force && Date.now() - lastSessionCheck < 60000) return;
    sessionTask = syncStorefrontSession().catch(error => { if (['login_required', 'login_invalid'].includes(error.message)) fail(error); }).finally(() => { sessionTask = null; });
  }
  function returnFromAccount() {
    accountNavigation = false;
    const pending = refreshTask || sessionTask;
    if (pending) pending.catch(() => {}).finally(() => refresh());
    else refresh();
  }
  window.addEventListener('pageshow', event => {
    if (event.persisted) ready.then(() => {
      if (accountNavigation) returnFromAccount();
      else checkSession(true);
    });
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) ready.then(() => {
    if (accountNavigation) returnFromAccount();
    else if (account.cachedLists?.({ allowExpired: true }) && !account.cachedLists()) refresh();
    else checkSession();
  }); });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const target = new URL(link.href, location.origin);
    if (target.origin === location.origin && /\/account\/logout\/?$/.test(target.pathname) || target.hostname === new URL(config.accountUrl).hostname && /\/(logout|signout)\/?$/.test(target.pathname)) {
      clearSignedOut(); channel?.postMessage({ type: 'logout' });
    } else if (target.hostname === new URL(config.accountUrl).hostname || target.origin === location.origin && /\/account\/?$/.test(target.pathname)) {
      // Account pages can sign out outside the theme. Never restore stale UI on return.
      accountNavigation = true; sessionGeneration++;
      account.invalidateLists(); storefrontData = undefined; catalog.clear();
      try { sessionStorage.removeItem('ucw-printer:list-cache:v1'); } catch {}
      publish({ status: 'loading', printers: [], error: '' });
    }
  }, true);
})();
